import { useEffect, useRef } from 'react';

// HOW FAR DOWN you were, kept across a refresh.
//
// The phases are long — Phase 2 is a parts table, a photograph gallery and a 3D panel — and
// a reload put you back at the top of it. The phase itself is remembered (see lastPlace);
// this is the other half of coming back to where you were.
//
// sessionStorage and not localStorage, unlike every other preference here. A scroll
// position belongs to a TAB and to the next few minutes: two windows on the same manual
// scroll independently, and a position from last Tuesday is not a place anybody is coming
// back to. It survives a reload, which is the whole of what was asked for, and nothing
// longer.
//
// THE BROWSER'S OWN RESTORATION IS TURNED OFF, because it cannot work here and would fight
// this one. It restores the moment the document is ready, when this app is an empty shell
// waiting on a document read, a hydrate and a PDF — the page is a few hundred pixels tall
// at that instant, so the scroll it restores is clamped to nearly zero and then the content
// arrives underneath it.
if (typeof history !== 'undefined' && 'scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}

const KEY = (key: string) => `notice.scroll.${key}`;

// How long to keep trying to get back down there. The page grows in stages — the stores
// hydrate, then the images land, then the PDF renders — and each stage makes it taller.
// Restoring once on mount lands at the bottom of a page that is still 800px tall.
const SETTLE_MS = 4000;

export function useScrollMemory(key: string | null) {
  // The position this hook has written for `key`, so the save loop can skip a write that
  // changes nothing — a scroll event fires per frame and sessionStorage is synchronous.
  const wrote = useRef(-1);

  useEffect(() => {
    if (!key) return;
    const want = Number(sessionStorage.getItem(KEY(key)) || 0);
    wrote.current = -1;

    let frame = 0;
    let stop = false;
    const started = Date.now();

    // GIVE UP THE MOMENT SOMEBODY SCROLLS. Landing a page back where it was is helpful;
    // pulling it out from under a hand that has started reading is not, and the two look
    // identical from here except for who moved first.
    const surrender = () => { stop = true; };

    const settle = () => {
      if (stop || !want) return;
      const room = document.documentElement.scrollHeight - window.innerHeight;
      // Only once the page is TALL ENOUGH to hold the position. Scrolling to 3000 on a
      // page that is 900 tall lands at 900, and the browser then has nothing left to do
      // when the rest arrives.
      if (room >= want) {
        window.scrollTo(0, want);
        stop = true;
        return;
      }
      // Partway there in the meantime, so a slow page moves down rather than sitting at
      // the top for four seconds.
      window.scrollTo(0, room);
      if (Date.now() - started > SETTLE_MS) return;
      frame = requestAnimationFrame(settle);
    };
    frame = requestAnimationFrame(settle);

    let pending = 0;
    const onScroll = () => {
      if (pending) return;
      // Coalesced to one write per frame. A scroll fires far more often than that, and
      // this is a synchronous write on the main thread.
      pending = requestAnimationFrame(() => {
        pending = 0;
        const y = Math.round(window.scrollY);
        if (y === wrote.current) return;
        wrote.current = y;
        sessionStorage.setItem(KEY(key), String(y));
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('wheel', surrender, { passive: true });
    window.addEventListener('touchstart', surrender, { passive: true });
    window.addEventListener('keydown', surrender);
    return () => {
      stop = true;
      cancelAnimationFrame(frame);
      if (pending) cancelAnimationFrame(pending);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('wheel', surrender);
      window.removeEventListener('touchstart', surrender);
      window.removeEventListener('keydown', surrender);
    };
  }, [key]);
}
